import React, { useState, useEffect } from 'react';
import {
  HiOutlinePlus,
  HiOutlineMagnifyingGlass,
  HiOutlinePauseCircle,
  HiOutlinePencil,
  HiOutlineTrash,
  HiOutlineXMark
} from 'react-icons/hi2';
import { authService } from '../../services';
import masterDataService from '../../services/masterDataService';
import SharedModal from '../../components/common/SharedModal';
import PageLoading from '../../components/common/PageLoading';

const PlannedDowntimes = () => {
  const [plannedDowntimes, setPlannedDowntimes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingPlannedDowntime, setEditingPlannedDowntime] = useState(null);
  const [formData, setFormData] = useState({
    pdName: '',
    pdDescription: '',
    status: 'Active',
  });
  const [formErrors, setFormErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  const tenantId = authService.getTenantId();

  useEffect(() => {
    if (tenantId) {
      loadPlannedDowntimes();
    } else {
      setError('Please login to view planned downtimes');
      setLoading(false);
    }
  }, [tenantId]);

  const loadPlannedDowntimes = async () => {
    try {
      setLoading(true);
      setError('');
      const data = await masterDataService.getPlannedDowntimes(tenantId);
      setPlannedDowntimes(data);
    } catch (err) {
      setError('Failed to load planned downtimes. Please try again.');
      console.error('Error loading planned downtimes:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
  };

  const filteredPlannedDowntimes = plannedDowntimes.filter((pd) =>
    pd.pdName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    pd.pdDescription?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleAdd = () => {
    setEditingPlannedDowntime(null);
    setFormData({
      pdName: '',
      pdDescription: '',
      status: 'Active',
    });
    setFormErrors({});
    setShowModal(true);
  };

  const handleEdit = (plannedDowntime) => {
    setEditingPlannedDowntime(plannedDowntime);
    setFormData({
      pdName: plannedDowntime.pdName || '',
      pdDescription: plannedDowntime.pdDescription || '',
      status: plannedDowntime.status || 'Active',
    });
    setFormErrors({});
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    try {
      setError('');
      await masterDataService.deletePlannedDowntime(id);
      await loadPlannedDowntimes();
      setDeleteConfirm(null);
    } catch (err) {
      setError('Failed to delete planned downtime. Please try again.');
      console.error('Error deleting planned downtime:', err);
    }
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.pdName.trim()) {
      errors.pdName = 'Planned downtime name is required';
    } else if (formData.pdName.length > 100) {
      errors.pdName = 'Name must be less than 100 characters';
    }
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    try {
      setSubmitting(true);
      setError('');

      const plannedDowntimeData = {
        tenantId: tenantId,
        pdName: formData.pdName.trim(),
        pdDescription: formData.pdDescription.trim(),
        status: formData.status,
      };

      if (editingPlannedDowntime) {
        plannedDowntimeData.pdId = editingPlannedDowntime.pdId;
        await masterDataService.updatePlannedDowntime(editingPlannedDowntime.pdId, plannedDowntimeData);
      } else {
        await masterDataService.createPlannedDowntime(plannedDowntimeData);
      }

      await loadPlannedDowntimes();
      setShowModal(false);
      setFormData({ pdName: '', pdDescription: '', status: 'Active' });
      setEditingPlannedDowntime(null);
    } catch (err) {
      const errorMessage = err.response?.data?.message || 
                          err.response?.data?.errors || 
                          err.message || 
                          'Failed to save planned downtime. Please try again.';
      setError(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingPlannedDowntime(null);
    setFormData({ pdName: '', pdDescription: '', status: 'Active' });
    setFormErrors({});
  };

  if (loading) {
    return <PageLoading message="Loading planned downtimes..." />;
  }

  return (
    <div className="planned-downtimes-page">
      <div className="page-header">
        <div className="header-content">
          <h2>Planned Downtimes</h2>
          <p>Manage scheduled downtime activities</p>
        </div>
        <button className="btn-primary" onClick={handleAdd}>
          <HiOutlinePlus className="btn-icon" />
          Add Planned Downtime
        </button>
      </div>

      {error && <div className="error-message">{error}</div>}

      <div className="table-container">
        <div className="table-header">
          <div className="search-box">
            <HiOutlineMagnifyingGlass className="search-icon" />
            <input
              type="text"
              placeholder="Search planned downtimes..."
              value={searchTerm}
              onChange={handleSearch}
              className="search-input"
            />
          </div>
          <div className="table-info">
            {filteredPlannedDowntimes.length} {filteredPlannedDowntimes.length === 1 ? 'planned downtime' : 'planned downtimes'}
          </div>
        </div>

        {filteredPlannedDowntimes.length === 0 ? (
          <div className="empty-state">
            <HiOutlinePauseCircle className="empty-icon" />
            <h3>No planned downtimes found</h3>
            <p>
              {searchTerm
                ? 'Try adjusting your search terms'
                : 'Get started by creating your first planned downtime'}
            </p>
            {!searchTerm && (
              <button className="btn-primary" onClick={handleAdd}>
                New Planned Downtime
              </button>
            )}
          </div>
        ) : (
          <table className="data-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>Name</th>
                <th>Description</th>
                <th>Status</th>
                <th>Created At</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredPlannedDowntimes.map((pd) => (
                <tr key={pd.pdId}>
                  <td>{pd.pdId}</td>
                  <td className="name-cell">{pd.pdName}</td>
                  <td>{pd.pdDescription || '-'}</td>
                  <td>
                    <span className={`status-badge ${pd.status?.toLowerCase()}`}>
                      {pd.status || 'Active'}
                    </span>
                  </td>
                  <td>
                    {pd.createdAt
                      ? new Date(pd.createdAt).toLocaleDateString()
                      : '-'}
                  </td>
                  <td>
                    <div className="action-buttons">
                      <button
                        className="btn-icon-edit"
                        onClick={() => handleEdit(pd)}
                        title="Edit"
                      >
                        <HiOutlinePencil />
                      </button>
                      <button
                        className="btn-icon-delete"
                        onClick={() => setDeleteConfirm(pd)}
                        title="Delete"
                      >
                        <HiOutlineTrash />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Create/Edit Modal */}
      <SharedModal
        isOpen={showModal}
        onClose={handleCloseModal}
        title={editingPlannedDowntime ? 'Edit Planned Downtime' : 'New Planned Downtime'}
        footer={
          <>
            <button type="button" className="btn-secondary" onClick={handleCloseModal} disabled={submitting}>Cancel</button>
            <button type="submit" form="planned-downtime-form" className="btn-primary" disabled={submitting}>
              {submitting ? (editingPlannedDowntime ? 'Updating...' : 'Creating...') : (editingPlannedDowntime ? 'Update' : 'Create')}
            </button>
          </>
        }
      >
        <div className="shared-modal-body">
          <form id="planned-downtime-form" onSubmit={handleSubmit} className="modal-form">
            <div className="form-group">
              <label htmlFor="pdName">
                Name <span className="required">*</span>
              </label>
              <input
                type="text"
                id="pdName"
                value={formData.pdName}
                onChange={(e) =>
                  setFormData({ ...formData, pdName: e.target.value })
                }
                className={formErrors.pdName ? 'error' : ''}
                placeholder="e.g., Preventive Maintenance, Lunch Break"
                maxLength={100}
              />
              {formErrors.pdName && (
                <span className="field-error">{formErrors.pdName}</span>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="pdDescription">Description</label>
              <textarea
                id="pdDescription"
                value={formData.pdDescription}
                onChange={(e) =>
                  setFormData({ ...formData, pdDescription: e.target.value })
                }
                placeholder="Enter description (optional)"
                rows="3"
              />
            </div>

            <div className="form-group">
              <label htmlFor="status">Status</label>
              <select
                id="status"
                value={formData.status}
                onChange={(e) =>
                  setFormData({ ...formData, status: e.target.value })
                }
              >
                <option value="Active">Active</option>
                <option value="Inactive">Inactive</option>
              </select>
            </div>

          </form>
        </div>
      </SharedModal>

      {/* Delete Confirmation Modal */}
      <SharedModal
        isOpen={!!deleteConfirm}
        onClose={() => setDeleteConfirm(null)}
        title="Confirm Delete"
        customClass="small"
        footer={
          <>
            <button
              type="button"
              className="btn-secondary"
              onClick={() => setDeleteConfirm(null)}
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn-danger"
              onClick={() => handleDelete(deleteConfirm.pdId)}
            >
              Delete
            </button>
          </>
        }
      >
        <div className="shared-modal-body">
          <p>
            Are you sure you want to delete{' '}
            <strong>{deleteConfirm?.pdName}</strong>?
          </p>
          <p className="warning-text">
            This action cannot be undone. The planned downtime will be marked as inactive.
          </p>
        </div>
      </SharedModal>
    </div>
  );
};

export default PlannedDowntimes;

